import { useLocation, useNavigate } from "react-router-dom";
import { JournalAttribute, useGetAllJournalEntriesFunctionRequest, useDeleteJournalEntryFunctionRequest } from "shared/request/myHealthyAdvantageApi";
import { Chip } from "shared/UI/Chip/Chip";
import { ContextMenu, ContextMenuListItem } from "shared/UI/ContextMenu/ContextMenu";
import { RoundedCard } from "shared/UI/RoundedCard/RoundedCard";
import { PageHeader } from "UIPalette/PageHeader/PageHeader";
import { useTranslation } from "react-i18next";
import { Error404 } from "../../../../UIPalette/Error404/Error404";
import PageTitle from "UIPalette/PageTitle/PageTitle";
import { useNavBarContext } from "core/state/NavBarContext/NavBarContext";
import { useStatusModal } from "shared/core/hooks/useStatusModal";
import { ClientInfoState } from "shared/core/state/clientInfoState";
import { useNotifyModalContext } from "shared/core/state/NotifyModalContext/NotifyModalContext";
import { PageLoader } from "UIPalette/PageLoader/PageLoader";
import { parseDateToString } from "core/helpers/dates";
import { trackEvents } from "core/monitoring/Events";
import { EventNames } from "core/monitoring/types/enums";
import { EntrySummaryCard } from "../../Components/EntrySummaryCard/EntrySummaryCard";
import { getSurroundingEntries } from "../../../../../../my-healthy-advantage/src/core/helpers/getSurroundingEntries";

export const JournalEntry = () => {
  const { state: journalEntryData } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setBody, Modal } = useStatusModal("delete-journal-entry");
  const { modalNotify } = useNotifyModalContext();
  const { trigger: deleteJournalEntry, isMutating } = useDeleteJournalEntryFunctionRequest({
    xClientCode: ClientInfoState.getCode(),
  });

  const { data: journalEntriesData } = useGetAllJournalEntriesFunctionRequest({
    xClientCode: ClientInfoState.getCode(),
  });

  const titleDate = parseDateToString(new Date(journalEntryData?.date ?? 0));

  useNavBarContext({ title: journalEntryData?.date ? titleDate : "" });

  if (!journalEntryData) {
    return <Error404 message={t("positiveYou.entryNotFound")} fullscreen={false} />;
  }

  const { attributes, content } = journalEntryData;

  const sortedEntries = [...(journalEntriesData?.entries ?? [])].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const surroundingEntries = getSurroundingEntries(sortedEntries, journalEntryData.id);

  const handleEditClick = () => {
    navigate("/positive-you-journal/edit", {
      state: journalEntryData,
      replace: true,
    });
  };

  const handleDeleteClick = () => {
    setBody({
      data: {
        title: t("positiveYou.delete.title"),
        cta: t("positiveYou.delete.cta"),
      },
      status: "error",
      callback: async () => {
        trackEvents(EventNames.DELETE_POSITIVE_YOU);
        try {
          await deleteJournalEntry({
            query: {
              id: journalEntryData.id,
            },
          });
          modalNotify("success", t("positiveYou.delete.successMessage"));
          navigate(-1);
        } catch (error) {
          modalNotify("error", t("positiveYou.delete.errorMessage"));
        }
      },
      cancelCallback: () => setBody(null),
    });
  };

  const deletingJournalDisplay = (
    <div className="p-6">
      <PageLoader title={t("positiveYou.delete.loading")} />
    </div>
  );

  const entryDetailsDisplay = (
    <>
      <div className="space-y-4 p-6">
        <div className="flex w-full justify-between">
          <h3>{t("positiveYou.entryContentTitle")}</h3>
          <ContextMenu menuPosition="left" aria-label={""}>
            <ContextMenuListItem text={"Edit"} onClick={handleEditClick} />
            <ContextMenuListItem text={"Delete"} onClick={handleDeleteClick} />
          </ContextMenu>
        </div>
        <p className="whitespace-pre-line">{content}</p>
      </div>
      <hr className="text-brand-grey-light" />
      <div className="p-6 space-y-2">
        <h3>{t("positiveYou.entryAttributesTitle")}</h3>
        <div className="flex flex-wrap gap-2">
          {attributes && attributes.length > 0
            ? attributes.map((attribute: JournalAttribute) => <Chip key={attribute.id} text={attribute.key} theme="secondary" />)
            : t("positiveYou.entryAttributesEmpty")}
        </div>
      </div>
    </>
  );

  const surroundingEntriesDisplay =
    surroundingEntries.length > 0 ? (
      <div className="mt-6">
        <hr className="my-2  text-brand-grey-light"></hr>
        <h3 className="mb-2 mt-3">{t("pageTitle.positiveYou.surroundingEntries.title")}</h3>
        <div className="flex gap-4 overflow-x-auto pb-2 lg:grid lg:grid-cols-3 lg:overflow-visible">
          {surroundingEntries.map((entry) => (
            <div key={entry.id} className="min-w-[280px] lg:min-w-0">
              <EntrySummaryCard
                className="min-h-[150px]"
                date={entry.date}
                content={entry.content}
                attributes={entry.attributes}
                onClick={() =>
                  navigate("/positive-you-journal/entry", {
                    state: entry,
                    replace: true,
                  })
                }
              />
            </div>
          ))}
        </div>
      </div>
    ) : null;

  return (
    <>
      <PageTitle title={t("pageTitle.positiveYou.entry", { date: titleDate })} />
      <PageHeader title={titleDate} hideForMobile />
      <RoundedCard className="!p-0"> {isMutating ? deletingJournalDisplay : entryDetailsDisplay}</RoundedCard>
      {surroundingEntriesDisplay}
      {Modal}
    </>
  );
};
